import React, { useState, useEffect, useRef } from "react";
import { useCart } from "../context/cartcontext.js";
import "../styles/sonstige/kasse.css";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { loadStripe } from "@stripe/stripe-js";
import {
    Elements,
    PaymentRequestButtonElement,
    useStripe
} from "@stripe/react-stripe-js";

const stripePromise = loadStripe(
    'pk_live_51RYwlIF2sDHWACYzxXcMQwCf6mYyI1CCaJbvc98sbWfo0FLus6L4FgkZrEBQ0VUgDP76AuB3lTIuaGp2yiGOz3N800NkLHmPqR'
);

function Body_kasse() {
    const { cart, cartTotal, removeFromCart, updateQuantity, clearCart } = useCart();
    const [paymentMethod, setPaymentMethod] = useState("paypal");
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [canPay, setCanPay] = useState(false);
    const stripe = useStripe();
    const clientSecretRef = useRef(null);

    useEffect(() => {
        const setupApplePay = async () => {
            if (!stripe || cartTotal <= 0) return;

            const email = localStorage.getItem("email") || "kunde@beispiel.de";
            const body = {
                amount: Math.round(cartTotal * 100),
                email,
                name: "ApplePay-Kunde",
                orderId: "AP" + Date.now(),
                items: cart.map(i => ({
                    name: i.name,
                    quantity: i.quantity,
                    price: i.price
                }))
            };

            try {
                const res = await fetch("https://nurleder.de/api/create-payment-intent/", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body)
                });
                const data = await res.json();
                const clientSecret = data.clientSecret;

                if (!clientSecret || !clientSecret.startsWith("pi_")) {
                    console.error("❌ Kein gültiger clientSecret erhalten:", data);
                    return;
                }

                clientSecretRef.current = clientSecret;

                const pr = stripe.paymentRequest({
                    country: "DE",
                    currency: "eur",
                    total: { label: "Gesamtbetrag", amount: Math.round(cartTotal * 100) },
                    requestPayerName: true,
                    requestPayerEmail: true
                });

                const result = await pr.canMakePayment();
                if (!result || !result.applePay) {
                    setCanPay(false);
                    return;
                }

                setCanPay(true);
                setPaymentRequest(pr);

                pr.on("paymentmethod", async (ev) => {
                    const secret = clientSecretRef.current;
                    if (!secret) {
                        console.error("⛔ Kein clientSecret gespeichert.");
                        ev.complete("fail");
                        return;
                    }

                    let confirm = await stripe.confirmCardPayment(secret, {
                        payment_method: ev.paymentMethod.id,
                        receipt_email: email
                    });

                    if (confirm.error && confirm.error.payment_intent?.status === "requires_action") {
                        confirm = await stripe.confirmCardPayment(secret);
                    }

                    if (confirm.error) {
                        console.error("❌ Stripe-Fehler:", confirm.error);
                        ev.complete("fail");
                        alert(`❌ Zahlung fehlgeschlagen: ${confirm.error.message}`);
                        return;
                    }

                    ev.complete("success");
                    alert("✅ Zahlung erfolgreich mit Apple Pay!");
                    clearCart();
                    sendInvoice(ev.payerEmail || email);
                });
            } catch (err) {
                console.error("❌ Fehler beim Setup von Apple Pay:", err);
            }
        };

        setupApplePay();
    }, [stripe, cart, cartTotal, clearCart]);

    const sendInvoice = (email) => {
        const invoiceData = {
            email,
            items: cart.map(i => ({
                name: i.name,
                quantity: i.quantity,
                price: i.price
            })),
            total: cartTotal.toFixed(2)
        };

        fetch("https://nurleder.de/api/send-invoice/", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(invoiceData)
        })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    console.log("📧 Rechnung gesendet an:", email);
                } else {
                    console.error("❌ Fehler beim Senden:", data.error);
                }
            })
            .catch(err => console.error("❌ API-Fehler:", err));
    };

    return (
        <section className="warenkorb">
            <h2>Kasse</h2>

            {cart.map((item) => (
                <div key={item.id} className="cart-item">
                    <img src={item.image} alt={item.name} className="cart-image" />
                    <div className="cart-info">
                        <p className="cart-name">{item.name}</p>
                        <label className="cart-name label">
                            Anzahl:
                            <select
                                value={item.quantity}
                                onChange={(e) => updateQuantity(item.id, parseInt(e.target.value))}
                            >
                                {[...Array(10).keys()].map(n => (
                                    <option key={n + 1} value={n + 1}>{n + 1}</option>
                                ))}
                            </select>
                        </label>
                        <p>Preis: {parseFloat(item.price).toFixed(2)} €</p>
                        <p>Summe: {(parseFloat(item.price) * item.quantity).toFixed(2)} €</p>
                        <button className="remove-button" onClick={() => removeFromCart(item.id)}>❌ Löschen</button>
                    </div>
                </div>
            ))}

            <h3 className="cart-total">Summe: {cartTotal.toFixed(2)} €</h3>

            <div className="payment-methods">
                <h4>💳 Zahlungsmethode:</h4>
                <select className="payment-select" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                    <option value="paypal">PayPal</option>
                    <option value="applepay">Apple Pay</option>
                </select>

                {paymentMethod === "paypal" && (
                    <div className="paypal-button-wrapper">
                        <PayPalButtons
                            style={{ layout: "horizontal", color: "gold", shape: "pill" }}
                            createOrder={(data, actions) => actions.order.create({
                                purchase_units: [{ amount: { value: cartTotal.toFixed(2), currency_code: "EUR" } }]
                            })}
                            onApprove={(data, actions) => actions.order.capture().then((details) => {
                                alert(`✅ Vielen Dank, ${details.payer.name.given_name}!`);
                                sendInvoice(details?.payer?.email_address || "kunde@beispiel.de");
                                clearCart();
                            })}
                            onError={(err) => {
                                console.error("PayPal Fehler:", err);
                                alert("❌ Fehler bei PayPal Zahlung");
                            }}
                        />
                    </div>
                )}

                {paymentMethod === "applepay" && canPay && paymentRequest && (
                    <div className="apple-pay-wrapper">
                        <PaymentRequestButtonElement options={{ paymentRequest }} />
                    </div>
                )}

                {paymentMethod === "applepay" && !canPay && (
                    <p style={{ color: "#999", textAlign: "center" }}>❌ Apple Pay wird auf diesem Gerät nicht unterstützt.</p>
                )}
            </div>

            <button onClick={() => (window.location.href = "/")} className="back-to-shop-btn">
                ⬅️ Zurück zum Shop
            </button>
        </section>
    );
}

function Body_kasse_wrapper() {
    return (
        <Elements stripe={stripePromise}>
            <PayPalScriptProvider options={{ "client-id": "AVkDO1DJaJg48XV84egKpvmV3Pm3_b96DBeBIsRH0ApeuCAjz7hArgHufelM13na2242EhSgLw1PhpUo", currency: "EUR" }}>
                <Body_kasse />
            </PayPalScriptProvider>
        </Elements>
    );
}

export default Body_kasse_wrapper;
